'use client';
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface CreatePostFormProps {
  onPostCreated?: (postId?: string, postSlug?: string) => void;
}

export default function CreatePostForm({ onPostCreated }: CreatePostFormProps) {
  const { user } = useAuth();
  const router = useRouter();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const MAX = 120;

  // After login, auto-submit if there's a pending post
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem('pending_post');
    if (!pending) return;
    localStorage.removeItem('pending_post');
    const pendingContent = pending.trim();
    if (pendingContent.length < 5) return;
    setContent(pendingContent);
    // Auto-submit
    (async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/post', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: pendingContent }),
          credentials: 'include',
        });
        const json = await res.json();
        if (!res.ok) {
          setError(json.error ?? 'Failed to post.');
          return;
        }
        setContent('');
        onPostCreated?.(json.post?.id, json.post?.slug ?? undefined);
      } catch (err: any) {
        setError(err.message ?? 'Failed to post.');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const handlePost = async () => {
    if (content.trim().length < 5) {
      setError('Too short — say more.');
      return;
    }
    if (!user) {
      // Save content and redirect to login — post will auto-submit after login
      localStorage.setItem('pending_post', content.trim());
      router.push('/sign-up-login-screen');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: content.trim() }),
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error ?? 'Failed to post.');
        return;
      }
      setContent('');
      onPostCreated?.(json.post?.id, json.post?.slug ?? undefined);
    } catch (err: any) {
      setError(err.message ?? 'Failed to post.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
      {/* Hero headline */}
      <h1 className="text-2xl font-extrabold text-foreground tracking-tight mb-3">
        Prove them wrong.
      </h1>

      <div className="relative">
        <textarea
          rows={3}
          maxLength={MAX}
          value={content}
          onChange={(e) => { setContent(e.target.value); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey) handlePost(); }}
          placeholder="What are you willing to be wrong about?"
          className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-150"
        />
        <div className="absolute top-2 right-2">
          <button
            type="button"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            className="w-4 h-4 rounded-full bg-muted text-muted-foreground text-[10px] font-bold flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors cursor-help"
            aria-label="Posting rules"
          >
            ?
          </button>
          {showTooltip && (
            <div className="absolute right-0 top-6 z-50 w-56 rounded-lg border border-border bg-popover shadow-lg p-3 text-xs text-popover-foreground">
              <p className="font-semibold mb-1.5 text-foreground">Posting rules</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Cannot be edited after posting</li>
                <li>• Max 120 characters</li>
                <li>• Minimum 5 characters</li>
                <li>• Be respectful and constructive</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-1.5">Say it. Back it. Or get proven wrong.</p>

      <div className="flex items-center justify-between mt-2">
        <div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs tabular-nums ${content.length > MAX * 0.9 ? 'text-red-500' : 'text-muted-foreground'}`}>
            {content.length}/{MAX}
          </span>
          <button
            onClick={handlePost}
            disabled={loading || content.trim().length === 0}
            className="flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-semibold px-5 py-2 rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-95"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : null}
            Call it
          </button>
        </div>
      </div>

      <div className="mt-2 space-y-0.5">
        <p className="text-xs text-muted-foreground">Example: BTC will hit 70k today</p>
        <p className="text-xs text-muted-foreground font-medium">People will disagree. Good.</p>
      </div>
    </div>
  );
}